import cogoToast from 'cogo-toast';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from 'react-query';
import { StatusResponse } from '../../../../../../../types/API';
import { getUseAdvisoryReportQKey } from '../../../../api/getAdvisoryReport';
import { AdvisoryReport } from '../../../../types/AdvisoryReport';
import { putInsurancePolicyOrderByReportId } from '../current-insurances/changeInsurancePolicyOrder';

interface Params {
  reportId: string;
  packageId: string;
  insurance_policies?: string[];
}

export const useChangeInsurancePolicyPackageOrder = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation<StatusResponse, unknown, Params, AdvisoryReport>(
    ({ insurance_policies, reportId }) =>
      putInsurancePolicyOrderByReportId({
        current_insurances: insurance_policies,
        reportId,
      }),
    {
      onMutate: async ({ reportId, packageId, insurance_policies }) => {
        const qKey = getUseAdvisoryReportQKey(reportId);
        const prevAdvisoryReport =
          queryClient.getQueryData<AdvisoryReport>(qKey);
        const prevPackage = prevAdvisoryReport?.policy_packages?.find(
          ({ id }) => id === packageId,
        );

        const newPackagePolicies = insurance_policies
          ? prevPackage?.insurance_policies?.sort(
              (a, b) =>
                insurance_policies.indexOf(a.id) -
                insurance_policies.indexOf(b.id),
            )
          : prevPackage?.insurance_policies;

        await queryClient.cancelQueries(qKey);
        const newPackages = prevAdvisoryReport?.policy_packages?.map(
          (packageObj) => {
            if (packageObj.id === prevPackage?.id)
              return { ...prevPackage, insurance_policies: newPackagePolicies };
            return packageObj;
          },
        );

        queryClient.setQueryData(qKey, {
          ...prevAdvisoryReport,
          policy_packages: newPackages,
        });

        return prevAdvisoryReport;
      },
      onError: (err, { reportId }, context) => {
        const qKey = getUseAdvisoryReportQKey(reportId);
        cogoToast.error(t('ADVISORY-REPORTS_ORDER-PACKAGE-FAILED'));
        if (context) queryClient.setQueryData(qKey, context);
      },
    },
  );
};
